import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useToast } from '@/shared/ui/Toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { APIProvider, Map, AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import { Star, Check } from 'lucide-react';
import { useLocation } from '@/shared/hooks/useLocation';
import { competitorApi, type Competitor, type CompetitorAnalysisData } from '../api/competitorApi';
import { uploadApi } from '../api/uploadApi';
import { Card, Badge, Button, Callout, EmptyState, Stat } from '@/shared/ui';
import { useThemeContext } from '@/shared/context/useThemeContext';
import { useWorkflowStatus, workflowsKey } from '@/shared/hooks/useWorkflows';
import { formatTimestamp } from '@/shared/utils/format';
import { strategyKeys } from '../queryKeys';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from '../components/theme.module.css';
import cs from './CompetitionStep.module.css';

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY ?? '';
const MAP_ID = import.meta.env.VITE_GOOGLE_MAP_ID ?? '';

type ThreatLevel = 'HIGH' | 'MED' | 'LOW';

function getThreatLevel(competitor: Pick<Competitor, 'rating' | 'reviewCount'>): ThreatLevel {
  if (!competitor.rating || !competitor.reviewCount) return 'LOW';
  if (competitor.reviewCount >= 300 && competitor.rating >= 4.5) return 'HIGH';
  if (competitor.reviewCount >= 100 && competitor.rating >= 4.0) return 'MED';
  return 'LOW';
}

function threatBadgeClass(level: ThreatLevel): string {
  if (level === 'HIGH') return cs.threatBadgeHigh;
  if (level === 'MED') return cs.threatBadgeMed;
  return cs.threatBadgeLow;
}

function PinMarker({ color }: { color: string }) {
  return (
    <svg width="28" height="40" viewBox="0 0 24 36" fill="none">
      <path
        d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 24 12 24s12-16.8 12-24C24 5.4 18.6 0 12 0zm0 16c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z"
        fill={color}
        stroke="var(--surface)"
        strokeWidth="1.5"
      />
    </svg>
  );
}

type MarkerVariant = 'direct' | 'indirect' | 'directSelected' | 'indirectSelected';

function CircleMarker({ variant }: { variant: MarkerVariant }) {
  const variantClass: Record<MarkerVariant, string> = {
    direct: cs.circleMarkerDirect,
    indirect: cs.circleMarkerIndirect,
    directSelected: cs.circleMarkerDirectSelected,
    indirectSelected: cs.circleMarkerIndirectSelected,
  };
  return <div className={variantClass[variant]} />;
}

const INFO_WINDOW_STYLE_ID = 'pn-competitor-map-infowindow';

function CompetitorMap({
  competitors,
  practiceCoords,
  practiceName,
  practiceAddress,
  isSpecialist,
  checkedIds,
}: {
  competitors: Competitor[];
  practiceCoords: {
    lat: number;
    lng: number;
    rating: number | null;
    reviewCount: number | null;
  } | null;
  practiceName?: string;
  practiceAddress?: string;
  isSpecialist: boolean;
  checkedIds: Set<string>;
}) {
  const { theme } = useThemeContext();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [cssInjected, setCssInjected] = useState(false);
  const [showDirect, setShowDirect] = useState(true);
  const [showIndirect, setShowIndirect] = useState(true);

  const INFO_WINDOW_CSS = `
    .gm-style-iw-c { background: transparent !important; box-shadow: none !important; padding: 0 !important; border-radius: 10px !important; overflow: visible !important; }
    .gm-style-iw-d { overflow: visible !important; }
    .gm-style-iw-tc::after { display: none !important; }
    .gm-style-iw-tc { display: none !important; }
    .gm-ui-hover-effect { top: 4px !important; right: 4px !important; }
    .gm-ui-hover-effect > span { background-color: var(--text-secondary) !important; }
  `;

  const injectCss = useCallback(() => {
    if (cssInjected) return;
    // Once per document: re-mounting the map must not stack <style> tags.
    if (!document.getElementById(INFO_WINDOW_STYLE_ID)) {
      const style = document.createElement('style');
      style.id = INFO_WINDOW_STYLE_ID;
      style.textContent = INFO_WINDOW_CSS;
      document.head.appendChild(style);
    }
    setCssInjected(true);
  }, [cssInjected, INFO_WINDOW_CSS]);

  const withCoords = competitors.filter((c) => c.latitude && c.longitude);

  const center = useMemo(() => {
    if (practiceCoords) return practiceCoords;
    if (!withCoords.length) return { lat: 37.97, lng: -122.03 };
    const lat = withCoords.reduce((s, c) => s + c.latitude!, 0) / withCoords.length;
    const lng = withCoords.reduce((s, c) => s + c.longitude!, 0) / withCoords.length;
    return { lat, lng };
  }, [withCoords, practiceCoords]);

  if (!MAPS_KEY) {
    return <div className={cs.mapNoKey}>Google Maps API key not configured</div>;
  }

  const selectedCompetitor =
    selectedId && selectedId !== 'practice'
      ? (withCoords.find((c) => c.id === selectedId) ?? null)
      : null;

  return (
    <APIProvider apiKey={MAPS_KEY}>
      <div className={cs.mapWrapper}>
        <Map
          className={cs.mapElement}
          defaultCenter={center}
          defaultZoom={12}
          mapId={MAP_ID || undefined}
          colorScheme={theme === 'light' ? 'LIGHT' : 'DARK'}
          disableDefaultUI
          zoomControl
          minZoom={8}
          backgroundColor="var(--bg)"
          onClick={() => setSelectedId(null)}
          onIdle={injectCss}
        >
          {/* Practice location — pin marker */}
          {practiceCoords && (
            <AdvancedMarker position={practiceCoords} onClick={() => setSelectedId('practice')}>
              <PinMarker color="var(--accent)" />
            </AdvancedMarker>
          )}

          {selectedId === 'practice' && practiceCoords && (
            <InfoWindow position={practiceCoords} onCloseClick={() => setSelectedId(null)}>
              <div className={cs.infoWindow}>
                <div className={cs.infoWindowName}>{practiceName || 'Your Practice'}</div>
                <div className={cs.infoWindowMeta}>
                  {practiceCoords.rating != null && (
                    <span className={cs.infoWindowRating}>
                      {practiceCoords.rating}
                      <Star size={11} fill="currentColor" aria-hidden />
                    </span>
                  )}
                  {practiceCoords.reviewCount != null && (
                    <span className={cs.infoWindowReviews}>
                      {practiceCoords.reviewCount} reviews
                    </span>
                  )}
                  <span className={cs.infoWindowPracticeBadge}>YOUR PRACTICE</span>
                </div>
                {practiceAddress && <div className={cs.infoWindowAddress}>{practiceAddress}</div>}
              </div>
            </InfoWindow>
          )}

          {/* Competitors — circle markers */}
          {withCoords.map((c) => {
            const isIndirect = isSpecialist && c.competitorLayer === 'INDIRECT';
            if (isSpecialist && (isIndirect ? !showIndirect : !showDirect)) return null;
            const isChecked = checkedIds.has(c.id);
            const variant: MarkerVariant = isIndirect
              ? isChecked
                ? 'indirectSelected'
                : 'indirect'
              : isChecked
                ? 'directSelected'
                : 'direct';
            return (
              <AdvancedMarker
                key={`${c.id}-${isChecked}`}
                position={{ lat: c.latitude!, lng: c.longitude! }}
                onClick={() => setSelectedId(c.id)}
              >
                <CircleMarker variant={variant} />
              </AdvancedMarker>
            );
          })}

          {selectedCompetitor && (
            <InfoWindow
              position={{ lat: selectedCompetitor.latitude!, lng: selectedCompetitor.longitude! }}
              onCloseClick={() => setSelectedId(null)}
            >
              <div className={cs.infoWindow}>
                <div className={cs.infoWindowName}>{selectedCompetitor.name}</div>
                <div className={cs.infoWindowMeta}>
                  {selectedCompetitor.rating != null && (
                    <span className={cs.infoWindowRating}>
                      {selectedCompetitor.rating}
                      <Star size={11} fill="currentColor" aria-hidden />
                    </span>
                  )}
                  {selectedCompetitor.reviewCount != null && (
                    <span className={cs.infoWindowReviews}>
                      {selectedCompetitor.reviewCount} reviews
                    </span>
                  )}
                  <span className={threatBadgeClass(getThreatLevel(selectedCompetitor))}>
                    {getThreatLevel(selectedCompetitor)}
                  </span>
                </div>
                {selectedCompetitor.address && (
                  <div className={cs.infoWindowAddress}>{selectedCompetitor.address}</div>
                )}
                {selectedCompetitor.distanceKm != null && (
                  <div className={cs.infoWindowDistance}>
                    {Math.round(selectedCompetitor.distanceKm * 0.621371 * 10) / 10} mi away
                  </div>
                )}
                {selectedCompetitor.website && (
                  <a
                    href={selectedCompetitor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cs.infoWindowLink}
                  >
                    Visit website ↗
                  </a>
                )}
              </div>
            </InfoWindow>
          )}
        </Map>
        {isSpecialist && (
          <div className={styles.mapLegend}>
            <div className={styles.mapLegendItem}>
              <svg className={styles.mapLegendPin} viewBox="0 0 24 36" fill="none">
                <path
                  d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 24 12 24s12-16.8 12-24C24 5.4 18.6 0 12 0zm0 16c-2.2 0-4-1.8-4-4s1.8-4 4-4 4 1.8 4 4-1.8 4-4 4z"
                  fill="var(--accent)"
                />
              </svg>
              Your Practice
            </div>
            <div
              className={showDirect ? styles.mapLegendToggle : styles.mapLegendHidden}
              onClick={() => setShowDirect((v) => !v)}
            >
              <span className={`${styles.mapLegendDot} ${cs.legendDotDirect}`} />
              Direct Competitor
            </div>
            <div
              className={showIndirect ? styles.mapLegendToggle : styles.mapLegendHidden}
              onClick={() => setShowIndirect((v) => !v)}
            >
              <span className={`${styles.mapLegendDot} ${cs.legendDotIndirect}`} />
              Indirect Competitor
            </div>
          </div>
        )}
      </div>
    </APIProvider>
  );
}

function hasCompetitorCoords(list: Array<{ latitude: number | null; longitude: number | null }>) {
  return list.some((c) => c.latitude && c.longitude);
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

const MAX_COMPETITORS = 10;
const INITIAL_VISIBLE = 20;
const INITIAL_LAYER_VISIBLE = 10;

// Discovery cap: max competitors stored in DB per layer
const MAX_DISCOVERY_CAP = 50;

function CompetitorList({
  competitors,
  selectedIds,
  layerSelectedCount,
  maxCount,
  onToggle,
}: {
  competitors: Array<Competitor & { distanceMiles: number | null }>;
  selectedIds: Set<string>;
  layerSelectedCount: number;
  maxCount: number;
  onToggle: (id: string) => void;
}) {
  return (
    <div className={cs.competitorList}>
      {competitors.map((comp) => {
        const isActive = selectedIds.has(comp.id);
        const atLimit = !isActive && layerSelectedCount >= maxCount;
        const itemClass = isActive
          ? atLimit
            ? cs.competitorItemActiveDisabled
            : cs.competitorItemActive
          : atLimit
            ? cs.competitorItemDisabled
            : cs.competitorItem;
        return (
          <div key={comp.id} onClick={() => !atLimit && onToggle(comp.id)} className={itemClass}>
            <span className={isActive ? cs.checkboxActive : cs.checkbox}>
              {isActive && <Check size={11} aria-hidden />}
            </span>
            <div className={cs.competitorBody}>
              <div className={cs.competitorNameRow}>
                <span className={cs.competitorName}>{comp.name}</span>
                {comp.distanceMiles != null && (
                  <span className={cs.competitorDistance}>{comp.distanceMiles.toFixed(1)} mi</span>
                )}
              </div>
              {comp.address && <div className={cs.competitorAddress}>{comp.address}</div>}
            </div>
            <div className={cs.competitorMeta}>
              {comp.rating != null && (
                <span className={cs.competitorRating}>
                  <Star size={11} fill="currentColor" aria-hidden /> {comp.rating.toFixed(1)}
                </span>
              )}
              {comp.reviewCount != null && (
                <span className={cs.competitorReviews}>{comp.reviewCount} reviews</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CompetitorLayerSection({
  title,
  description,
  competitors,
  selectedIds,
  layerSelectedCount,
  onToggle,
  expanded,
  onToggleExpand,
  displayRadius,
}: {
  title: string;
  description: string;
  competitors: Array<Competitor & { distanceMiles: number | null }>;
  selectedIds: Set<string>;
  layerSelectedCount: number;
  onToggle: (id: string) => void;
  expanded: boolean;
  onToggleExpand: () => void;
  displayRadius: number;
}) {
  const layerMax = MAX_COMPETITORS / 2;
  return (
    <>
      <div className={cs.layerHeader}>
        <span className={cs.layerTitle}>
          {title} ({competitors.length})
        </span>
        <span className={cs.layerCount}>
          {layerSelectedCount}/{layerMax} selected
        </span>
      </div>
      <div className={cs.layerDescription}>{description}</div>
      {competitors.length === 0 ? (
        <div className={cs.layerEmpty}>
          No {title.toLowerCase()} found within {displayRadius} miles
        </div>
      ) : (
        <>
          <CompetitorList
            competitors={expanded ? competitors : competitors.slice(0, INITIAL_LAYER_VISIBLE)}
            selectedIds={selectedIds}
            layerSelectedCount={layerSelectedCount}
            maxCount={layerMax}
            onToggle={onToggle}
          />
          {competitors.length > INITIAL_LAYER_VISIBLE && (
            <div className={cs.showMoreWrap}>
              <button onClick={onToggleExpand} className={cs.showMoreBtn}>
                {expanded ? 'Show less' : `Show all ${competitors.length}`}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}

export function CompetitionStep() {
  const { showToast } = useToast();
  const { registerOnBeforeNext } = useOutletContext<{
    registerOnBeforeNext: (fn: (() => void | Promise<void>) | null) => void;
  }>();
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();

  // Slow work runs as background jobs; the page watches each one's workflow.
  const { isRunning: analysisWorkflowRunning } = useWorkflowStatus(
    locationId,
    'competitive_analysis',
    [[...strategyKeys.competitorAnalysis(locationId)], [...strategyKeys.competitors(locationId)]],
    {
      onFailed: (error) => {
        setActiveTab('competitors');
        showToast(error || "Couldn't generate competitive insights. Please try again.", 'error');
      },
    },
  );
  const { isRunning: discoveryRunning, latest: latestDiscovery } = useWorkflowStatus(
    locationId,
    'competitor_discovery',
    [[...strategyKeys.competitors(locationId)], [...strategyKeys.competitorsDensity(locationId)]],
  );
  // A failed scan isn't retried on page loads for a while (each one is a paid
  // Places scan); the page says so and offers Retry.
  const { isRunning: densityRunning, isFailed: densityFailed } = useWorkflowStatus(
    locationId,
    'competition_density',
    [[...strategyKeys.competitorsDensity(locationId)]],
  );

  // ─── Radius state (same pattern as MarketIntelligenceStep) ────────────────
  const { data: radiusRes } = useQuery({
    queryKey: strategyKeys.radius(locationId),
    queryFn: () => uploadApi.getRadius(locationId),
    enabled: !!locationId,
  });
  const specialty = radiusRes?.data?.specialty ?? '';
  // Resolved by the backend against the practice's vertical.
  const isSpecialist = radiusRes?.data?.isSpecialist ?? false;
  const maxRadius = isSpecialist ? 60 : 20;
  const radius = radiusRes?.data?.radius ?? (isSpecialist ? 20 : 5);
  const [pendingRadius, setPendingRadius] = useState<number | null>(null);
  const displayRadius = pendingRadius ?? radius;

  if (pendingRadius != null && pendingRadius === radius) {
    setPendingRadius(null);
  }

  // ─── Competition density ──────────────────────────────────────────────────
  const {
    data: densityRes,
    isLoading: densityLoading,
    isFetching: densityFetching,
  } = useQuery({
    queryKey: strategyKeys.competitorsDensity(locationId),
    queryFn: async () => {
      const res = await competitorApi.getDensity(locationId);
      // No scan yet for this radius (or an expired one): the server queued
      // one — start watching it.
      if (!res.data || res.data.refreshing) {
        queryClient.invalidateQueries({
          queryKey: workflowsKey(locationId, 'competition_density'),
        });
      }
      return res;
    },
    enabled: !!locationId,
    // A density scan can take minutes; a client-side retry would start a
    // second full scan while the first is still running on the server.
    retry: false,
  });
  const density = densityRes?.data ?? null;
  // The scan runs as a background job; show progress until it lands.
  const densityBusy = densityFetching || densityRunning;

  const densityScanMutation = useMutation({
    mutationFn: () => competitorApi.scanDensity(locationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: workflowsKey(locationId, 'competition_density'),
      });
    },
    onError: (err: unknown) => {
      showToast((err as Error).message || "Couldn't start the scan. Please try again.", 'error');
    },
  });

  const { data: competitorsRes, isLoading } = useQuery({
    queryKey: strategyKeys.competitors(locationId),
    queryFn: () => competitorApi.getCompetitors(locationId),
    enabled: !!locationId,
  });

  const { data: coordsRes } = useQuery({
    queryKey: strategyKeys.practiceCoords(locationId),
    queryFn: () => competitorApi.getPracticeCoords(locationId),
    enabled: !!locationId,
  });

  const {
    mutate: discover,
    isPending: discoverQueueing,
    error: discoverRequestError,
  } = useMutation({
    mutationFn: () => competitorApi.discoverCompetitors(locationId),
    onSuccess: async () => {
      // Discovery runs in the background; watch its workflow from here.
      await queryClient.refetchQueries({
        queryKey: workflowsKey(locationId, 'competitor_discovery'),
      });
      queryClient.invalidateQueries({ queryKey: strategyKeys.competitorsDensity(locationId) });
    },
  });

  const radiusTimer = useRef<ReturnType<typeof setTimeout>>();
  const discoverTimer = useRef<ReturnType<typeof setTimeout>>();
  // The widest radius already searched. Seeded once the saved radius loads —
  // seeding it from the pre-load default made the first slider move on a
  // specialist trigger a needless discovery.
  const maxDiscoveredRadius = useRef<number | null>(null);
  if (maxDiscoveredRadius.current == null && radiusRes) {
    maxDiscoveredRadius.current = radius;
  }
  useEffect(
    () => () => {
      clearTimeout(radiusTimer.current);
      clearTimeout(discoverTimer.current);
    },
    [locationId],
  );
  const radiusMutation = useMutation({
    mutationFn: (r: number) => uploadApi.setRadius(locationId, r),
    onError: (err: unknown) => {
      setPendingRadius(null);
      showToast((err as Error).message || "Couldn't change the radius.", 'error');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: strategyKeys.competitorsDensity(locationId) });
      queryClient.invalidateQueries({ queryKey: strategyKeys.marketIntel(locationId) });
      queryClient.invalidateQueries({ queryKey: strategyKeys.radius(locationId) });
    },
  });
  const commitRadius = (val: number) => {
    // Save radius (debounced 1s)
    clearTimeout(radiusTimer.current);
    radiusTimer.current = setTimeout(() => radiusMutation.mutate(val), 1000);
    // Only discover if expanding beyond the max radius we've already searched
    // and we haven't hit the discovery cap yet
    clearTimeout(discoverTimer.current);
    if (val > (maxDiscoveredRadius.current ?? radius) && !atDiscoveryCap) {
      discoverTimer.current = setTimeout(() => {
        maxDiscoveredRadius.current = val;
        discover();
      }, 2000);
    }
  };

  // ─── Competitor selection state ──────────────────────────────────────────
  // Discovery is "in progress" from the click until its background job settles.
  const discovering = discoverQueueing || discoveryRunning;
  const discoverError: Error | null =
    (discoverRequestError as Error | null) ??
    (latestDiscovery?.status === 'failed'
      ? new Error(latestDiscovery.error ?? "Couldn't search for competitors.")
      : null);
  const isDiscovering = discovering || radiusMutation.isPending;

  // Auto-discover competitors on first visit (no competitors in DB yet)
  const rawCompetitors = competitorsRes?.data ?? [];

  // Check if discovery cap has been reached (50 per layer for specialists, 50 total for generalists)
  const atDiscoveryCap = useMemo(() => {
    if (isSpecialist) {
      const directCount = rawCompetitors.filter(
        (c) => c.competitorLayer === 'DIRECT' || !c.competitorLayer,
      ).length;
      const indirectCount = rawCompetitors.filter((c) => c.competitorLayer === 'INDIRECT').length;
      return directCount >= MAX_DISCOVERY_CAP && indirectCount >= MAX_DISCOVERY_CAP;
    }
    return rawCompetitors.length >= MAX_DISCOVERY_CAP;
  }, [rawCompetitors, isSpecialist]);

  const [autoDiscoverTriggered, setAutoDiscoverTriggered] = useState(false);
  if (
    !autoDiscoverTriggered &&
    !isLoading &&
    rawCompetitors.length === 0 &&
    !discovering &&
    locationId
  ) {
    setAutoDiscoverTriggered(true);
    discover();
  }

  const practiceCoords = coordsRes?.data ?? null;

  const allCompetitors = useMemo(() => {
    const radiusMiles = displayRadius;
    return [...(competitorsRes?.data ?? [])]
      .map((c) => ({
        ...c,
        distanceMiles: c.distanceKm != null ? c.distanceKm * 0.621371 : null,
      }))
      .filter((c) => {
        if (c.distanceMiles == null) return true;
        return c.distanceMiles <= radiusMiles;
      })
      .sort((a, b) => (a.distanceMiles ?? Infinity) - (b.distanceMiles ?? Infinity));
  }, [competitorsRes, displayRadius]);

  // For specialty practices, split competitors by layer
  const directCompetitors = useMemo(
    () =>
      isSpecialist
        ? allCompetitors.filter(
            (c) => c.competitorLayer === 'DIRECT' || (!c.competitorLayer && isSpecialist),
          )
        : [],
    [allCompetitors, isSpecialist],
  );
  const indirectCompetitors = useMemo(
    () => (isSpecialist ? allCompetitors.filter((c) => c.competitorLayer === 'INDIRECT') : []),
    [allCompetitors, isSpecialist],
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionInitialized, setSelectionInitialized] = useState(false);
  const [showAllCompetitors, setShowAllCompetitors] = useState(false);
  const [showAllDirect, setShowAllDirect] = useState(false);
  const [showAllIndirect, setShowAllIndirect] = useState(false);

  // Initialize selection from server state (competitors with status 'active').
  // Competitors default to status='active' on creation, so a prior save is
  // detected from the server's `reviewed` flag (or any dismissed row) — a
  // save that kept every competitor dismissed none, and used to be reset to
  // the default picks on the next visit.
  // Wait for radius to load before initializing — otherwise displayRadius defaults
  // cause inconsistent filtering of allCompetitors across page loads.
  const radiusLoaded = !!radiusRes;
  if (!selectionInitialized && allCompetitors.length > 0 && radiusLoaded) {
    const hasSavedSelection =
      !!competitorsRes?.reviewed || allCompetitors.some((c) => c.status === 'dismissed');
    if (isSpecialist) {
      if (hasSavedSelection) {
        // Rediscovery adds new rows as active, so clamp each layer to its
        // limit — the backend only analyzes that many per layer anyway.
        const perLayer = MAX_COMPETITORS / 2;
        const active = allCompetitors.filter((c) => c.status === 'active');
        const direct = active.filter((c) => c.competitorLayer !== 'INDIRECT').slice(0, perLayer);
        const indirect = active.filter((c) => c.competitorLayer === 'INDIRECT').slice(0, perLayer);
        setSelectedIds(new Set([...direct, ...indirect].map((c) => c.id)));
      } else {
        const directIds = directCompetitors.slice(0, 3).map((c) => c.id);
        const indirectIds = indirectCompetitors.slice(0, 3).map((c) => c.id);
        setSelectedIds(new Set([...directIds, ...indirectIds]));
      }
    } else {
      const activeIds = new Set(
        allCompetitors
          .filter((c) => c.status === 'active')
          .slice(0, MAX_COMPETITORS)
          .map((c) => c.id),
      );
      setSelectedIds(activeIds);
    }
    setSelectionInitialized(true);
  }

  const selectedCount = selectedIds.size;

  // Per-layer selection counts for specialty practices
  const directSelectedCount = useMemo(
    () =>
      isSpecialist
        ? [...selectedIds].filter((id) => {
            const c = allCompetitors.find((comp) => comp.id === id);
            return c?.competitorLayer === 'DIRECT' || (!c?.competitorLayer && isSpecialist);
          }).length
        : 0,
    [selectedIds, allCompetitors, isSpecialist],
  );
  const indirectSelectedCount = useMemo(
    () =>
      isSpecialist
        ? [...selectedIds].filter((id) => {
            const c = allCompetitors.find((comp) => comp.id === id);
            return c?.competitorLayer === 'INDIRECT';
          }).length
        : 0,
    [selectedIds, allCompetitors, isSpecialist],
  );

  const toggleCompetitor = useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else if (isSpecialist) {
          // Layer-aware limit: max 10 per layer
          const comp = allCompetitors.find((c) => c.id === id);
          const layer = comp?.competitorLayer;
          const layerCount = [...next].filter((sid) => {
            const sc = allCompetitors.find((c) => c.id === sid);
            return layer === 'INDIRECT'
              ? sc?.competitorLayer === 'INDIRECT'
              : sc?.competitorLayer !== 'INDIRECT';
          }).length;
          if (layerCount < MAX_COMPETITORS / 2) {
            next.add(id);
          }
        } else if (next.size < MAX_COMPETITORS) {
          next.add(id);
        }
        return next;
      });
    },
    [isSpecialist, allCompetitors],
  );

  // ─── Analysis (cache-only on load, explicit generation via button) ───────
  const { data: analysisRes } = useQuery({
    queryKey: strategyKeys.competitorAnalysis(locationId),
    queryFn: () => competitorApi.getAnalysis(locationId, true),
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000,
  });
  const analysis: CompetitorAnalysisData | null = analysisRes?.data ?? null;
  const hasAnalysisData = !!analysis?.insight;
  // The insights describe a different set of competitors than the one picked
  // now (saved or not), or the server flags them out of date.
  const analysisOutOfDate =
    hasAnalysisData &&
    !analysisWorkflowRunning &&
    (!!analysis?.isStale ||
      (selectionInitialized &&
        (analysis!.competitors.length !== selectedIds.size ||
          analysis!.competitors.some((c) => !selectedIds.has(c.id)))));

  // Analyzed competitors for the Insights tab — use the analysis response directly
  // so we only show competitors the backend actually analyzed. The LLM may
  // populate any subset of strengths/gap/threatRationale, so include the card
  // when any of them is present rather than gating on strengths alone.
  const analyzedCompetitors = useMemo(
    () =>
      (analysis?.competitors ?? [])
        .filter((c) => c.strengths != null || c.gap != null || c.threatRationale != null)
        .map((c) => ({
          ...c,
          distanceMiles: c.distanceKm != null ? c.distanceKm * 0.621371 : null,
          // Fill fields expected by threatLabel/threatColor
          status: 'active' as const,
          placeTypes: [] as string[],
          placeId: null,
          source: '',
          competitorLayer: c.competitorLayer ?? null,
          lastSyncedAt: null,
          createdAt: '',
          updatedAt: '',
          locationId: '',
          latitude: null,
          longitude: null,
          distanceKm: c.distanceKm,
        }))
        .sort((a, b) => (a.distanceMiles ?? Infinity) - (b.distanceMiles ?? Infinity)),
    [analysis],
  );

  // ─── Persisting the selection ────────────────────────────────────────────
  // Saved on Generate and on Next, so picks aren't lost when the user moves
  // on without generating (strategy generation reads the active set).
  const selectionDirty = useMemo(() => {
    if (!selectionInitialized) return false;
    return (competitorsRes?.data ?? []).some(
      (c) => (c.status === 'active') !== selectedIds.has(c.id),
    );
  }, [competitorsRes, selectedIds, selectionInitialized]);

  const saveSelection = useCallback(
    () => competitorApi.saveSelection(locationId, [...selectedIds]),
    [locationId, selectedIds],
  );

  const saveOnNextRef = useRef<() => Promise<void>>(async () => {});
  saveOnNextRef.current = async () => {
    if (!selectionDirty) return;
    try {
      await saveSelection();
      queryClient.invalidateQueries({ queryKey: strategyKeys.competitors(locationId) });
      // New picks can make the existing analysis out of date.
      queryClient.invalidateQueries({ queryKey: strategyKeys.competitorAnalysis(locationId) });
    } catch (err) {
      showToast((err as Error).message || "Couldn't save your competitor picks.", 'error');
      throw err;
    }
  };
  useEffect(() => {
    registerOnBeforeNext(() => saveOnNextRef.current());
    return () => registerOnBeforeNext(null);
  }, [registerOnBeforeNext]);

  // ─── Generate insights mutation ──────────────────────────────────────────
  const generateMutation = useMutation({
    mutationFn: async () => {
      // One write covering ALL competitors (not just radius-filtered), so
      // stale active competitors outside the current radius get dismissed.
      await saveSelection();
      return competitorApi.regenerateAnalysis(locationId);
    },
    onMutate: () => {
      setActiveTab('insights');
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: strategyKeys.competitors(locationId) });
      // The analysis runs in the background: pick up its workflow so the
      // Insights tab shows "generating" until it completes.
      await queryClient.refetchQueries({
        queryKey: workflowsKey(locationId, 'competitive_analysis'),
      });
    },
    onError: (err: unknown) => {
      setActiveTab('competitors');
      queryClient.invalidateQueries({ queryKey: strategyKeys.competitorAnalysis(locationId) });
      showToast(
        (err as Error).message || "Couldn't generate competitive insights. Please try again.",
        'error',
      );
    },
  });

  const generateBlocked =
    selectedCount === 0 || generateMutation.isPending || analysisWorkflowRunning;

  // ─── Insight feedback ────────────────────────────────────────────────────
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const MAX_FEEDBACK_WORDS = 75;

  const wordCount = feedbackText.trim() ? feedbackText.trim().split(/\s+/).length : 0;
  const overLimit = wordCount > MAX_FEEDBACK_WORDS;

  const feedbackMutation = useMutation({
    mutationFn: (feedback: string) => competitorApi.submitInsightFeedback(locationId, feedback),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: strategyKeys.competitorAnalysis(locationId) });
      setShowFeedback(false);
    },
    onError: (err: unknown) =>
      showToast((err as Error).message || "Couldn't save your feedback.", 'error'),
  });

  // ─── Tabs ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'competitors' | 'insights'>('competitors');

  // Competition density: fewer residents per practice = more competitive market
  const competitivenessData = useMemo(() => {
    if (!density) return null;
    const { residentsPerPractice, nationalBenchmark, practiceCount } = density;
    // No practices found: nothing to divide by — an open market, not "∞x".
    if (!practiceCount || residentsPerPractice <= 0 || nationalBenchmark <= 0) {
      return { display: 'Low', isMoreCompetitive: false, sub: 'No competing practices found' };
    }
    const isMoreCompetitive = residentsPerPractice < nationalBenchmark;
    const pct = Math.round(((nationalBenchmark - residentsPerPractice) / nationalBenchmark) * 100);

    // Use percentage when within ±99%, otherwise show ratio (e.g. "2.3x")
    const display =
      Math.abs(pct) <= 99
        ? `${pct > 0 ? '+' : ''}${pct}%`
        : isMoreCompetitive
          ? `${(nationalBenchmark / residentsPerPractice).toFixed(1)}x`
          : `${(residentsPerPractice / nationalBenchmark).toFixed(1)}x`;

    return {
      display,
      isMoreCompetitive,
      sub: isMoreCompetitive ? 'More competitive than avg' : 'Less competitive than avg',
    };
  }, [density]);

  if (isLoading) return <div className={cs.loadingState}>Loading...</div>;

  // Visible competitors for the list (collapse to first 20 unless expanded)
  const visibleCompetitors = showAllCompetitors
    ? allCompetitors
    : allCompetitors.slice(0, INITIAL_VISIBLE);
  const hasMoreCompetitors = allCompetitors.length > INITIAL_VISIBLE;

  return (
    <div className={cs.root}>
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <div className={cs.header}>
        <div className={cs.headerRow}>
          <h3 className={cs.headerTitle}>Competitive Landscape</h3>
          <div className={cs.headerControls}>
            <label className={cs.radiusLabel}>Radius</label>
            <input
              type="range"
              min={isSpecialist ? 5 : 2}
              max={maxRadius}
              value={displayRadius}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setPendingRadius(val);
                commitRadius(val);
              }}
              className={cs.radiusInput}
            />
            <span className={cs.radiusValue}>{displayRadius} mi</span>
          </div>
        </div>
        <p className={cs.headerSub}>
          {allCompetitors.length
            ? isSpecialist
              ? `${directCompetitors.length} direct + ${indirectCompetitors.length} indirect competitors within ${displayRadius} miles`
              : `${allCompetitors.length} competitors within ${displayRadius} miles of your location`
            : 'Discover competitors in your area'}
        </p>
        {atDiscoveryCap && (
          <p className={cs.discoveryCap}>
            {isSpecialist
              ? `Discovery limit reached (max ${MAX_DISCOVERY_CAP} direct + ${MAX_DISCOVERY_CAP} indirect)`
              : `Discovery limit reached (max ${MAX_DISCOVERY_CAP} competitors)`}
          </p>
        )}
        {analysis?.generatedAt && (
          <p className={patterns.timestamp}>
            Last updated: {formatTimestamp(analysis.generatedAt)}
          </p>
        )}
      </div>

      <>
        {/* ─── Density Stats ────────────────────────────────────────── */}
        {(density || densityBusy) && (
          <Card className={cs.densityCard}>
            <div className={densityBusy || !density ? cs.densityGridHidden : cs.densityGrid}>
              <Stat
                label="Service Area Pop."
                value={density ? fmt(density.population) : '—'}
                sub={`~${displayRadius} mi radius`}
              />
              <Stat
                label="Residents / Practice"
                value={
                  density && density.practiceCount > 0
                    ? density.residentsPerPractice.toLocaleString()
                    : '—'
                }
                sub={density ? `National avg: ${density.nationalBenchmark.toLocaleString()}` : '—'}
                tone={
                  density &&
                  density.practiceCount > 0 &&
                  density.residentsPerPractice < density.nationalBenchmark
                    ? 'error'
                    : 'accent'
                }
              />
              <Stat
                label="Market Competitiveness"
                value={competitivenessData?.display ?? '—'}
                sub={competitivenessData?.sub ?? '—'}
                tone={
                  competitivenessData
                    ? competitivenessData.isMoreCompetitive
                      ? 'error'
                      : 'accent'
                    : 'default'
                }
              />
            </div>
            {densityBusy && (
              <div className={cs.densitySpinner}>
                <div className={cs.spinnerLg} />
                <span className={cs.densitySpinnerText}>
                  {densityLoading
                    ? 'Loading competition density...'
                    : densityRunning && !density
                      ? 'Scanning your area for competing practices — this can take a few minutes...'
                      : 'Updating competition density...'}
                </span>
              </div>
            )}
          </Card>
        )}

        {densityFailed && !densityBusy && (
          <Callout
            variant="warning"
            title={
              density
                ? "We couldn't refresh competition density"
                : "We couldn't scan your area for competing practices"
            }
            actions={
              <Button
                size="sm"
                variant="secondary"
                onClick={() => densityScanMutation.mutate()}
                loading={densityScanMutation.isPending}
                disabled={densityScanMutation.isPending}
              >
                Retry scan
              </Button>
            }
          >
            {density
              ? 'The figures above are from the last successful scan.'
              : 'Competition density will appear here once a scan succeeds.'}
          </Callout>
        )}

        {/* ─── Map ─────────────────────────────────────────────────── */}
        <Card className={cs.mapCard} padding="none">
          {!practiceCoords && !hasCompetitorCoords(allCompetitors) ? (
            <EmptyState
              compact
              title="Map unavailable"
              description="We couldn't place your practice on the map. Check the address in your location settings."
            />
          ) : (
            <CompetitorMap
              competitors={allCompetitors}
              practiceCoords={practiceCoords}
              practiceName={currentLocation?.name}
              practiceAddress={[
                currentLocation?.address,
                currentLocation?.city,
                currentLocation?.state,
              ]
                .filter(Boolean)
                .join(', ')}
              isSpecialist={isSpecialist}
              checkedIds={selectedIds}
            />
          )}
        </Card>

        {/* ─── Tabs ────────────────────────────────────────────────── */}
        <div className={cs.tabs}>
          <button
            onClick={() => setActiveTab('competitors')}
            className={activeTab === 'competitors' ? cs.tabActive : cs.tab}
          >
            All Competitors ({allCompetitors.length})
          </button>
          {(hasAnalysisData || analysisWorkflowRunning || generateMutation.isPending) && (
            <button
              onClick={() => setActiveTab('insights')}
              className={activeTab === 'insights' ? cs.tabActive : cs.tab}
            >
              Competitive Insights
            </button>
          )}
        </div>

        {/* ─── Tab 1: All Competitors (selection) ──────────────────── */}
        {(activeTab === 'competitors' ||
          (activeTab === 'insights' &&
            !hasAnalysisData &&
            !analysisWorkflowRunning &&
            !generateMutation.isPending)) && (
          <>
            {allCompetitors.length === 0 ? (
              <Card className={cs.discoveringEmptyCard}>
                {discovering ? (
                  <div className={cs.discoveringEmptyInner}>
                    <div className={cs.spinnerMd} />
                    <span className={cs.discoveringEmptyText}>Discovering competitors...</span>
                  </div>
                ) : (
                  <EmptyState
                    compact
                    title={
                      discoverError
                        ? "Couldn't search for competitors"
                        : rawCompetitors.length > 0
                          ? `No competitors within ${displayRadius} miles`
                          : 'No competitors found yet'
                    }
                    description={
                      discoverError
                        ? (discoverError as Error).message ||
                          'Something went wrong searching your area. Try again in a moment.'
                        : rawCompetitors.length > 0
                          ? 'Widen the radius to include the practices we found further away.'
                          : 'We searched your area and found no matching practices. Try a wider radius or search again.'
                    }
                    action={
                      rawCompetitors.length > 0 && !discoverError
                        ? undefined
                        : { label: 'Search again', onClick: () => discover() }
                    }
                  />
                )}
              </Card>
            ) : (
              <>
                {isDiscovering && (
                  <div className={cs.discoveringBanner}>
                    <span className={cs.spinnerSm} />
                    Finding competitors...
                  </div>
                )}

                {isSpecialist ? (
                  <>
                    <CompetitorLayerSection
                      title="Direct Competitors"
                      description={`Other ${specialty.replace(/_/g, ' ')} practices you compete with directly`}
                      competitors={directCompetitors}
                      selectedIds={selectedIds}
                      layerSelectedCount={directSelectedCount}
                      onToggle={toggleCompetitor}
                      expanded={showAllDirect}
                      onToggleExpand={() => setShowAllDirect((prev) => !prev)}
                      displayRadius={displayRadius}
                    />
                    <div className={cs.indirectSeparator}>
                      <CompetitorLayerSection
                        title="Indirect Competitors"
                        description="General practices treating your patient pool"
                        competitors={indirectCompetitors}
                        selectedIds={selectedIds}
                        layerSelectedCount={indirectSelectedCount}
                        onToggle={toggleCompetitor}
                        expanded={showAllIndirect}
                        onToggleExpand={() => setShowAllIndirect((prev) => !prev)}
                        displayRadius={displayRadius}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className={cs.selectionHintRow}>
                      <span className={cs.selectionHint}>
                        Select up to {MAX_COMPETITORS} competitors for analysis ({selectedCount}/
                        {MAX_COMPETITORS} selected)
                      </span>
                    </div>
                    <CompetitorList
                      competitors={visibleCompetitors}
                      selectedIds={selectedIds}
                      layerSelectedCount={selectedCount}
                      maxCount={MAX_COMPETITORS}
                      onToggle={toggleCompetitor}
                    />

                    {/* Show all / collapse toggle */}
                    {hasMoreCompetitors && (
                      <div className={cs.showAllWrap}>
                        <button
                          onClick={() => setShowAllCompetitors((prev) => !prev)}
                          className={cs.showAllBtn}
                        >
                          {showAllCompetitors
                            ? 'Show less'
                            : `Show all ${allCompetitors.length} competitors`}
                        </button>
                      </div>
                    )}
                  </>
                )}

                {/* Generate button */}
                <div className={cs.generateWrap}>
                  <button
                    onClick={() => generateMutation.mutate()}
                    // Not while an analysis runs: Generate first saves the
                    // picks, which would change the set mid-analysis.
                    disabled={generateBlocked}
                    className={generateBlocked ? cs.generateBtnDisabled : cs.generateBtn}
                  >
                    {generateMutation.isPending || analysisWorkflowRunning
                      ? 'Generating...'
                      : hasAnalysisData
                        ? 'Regenerate Competitive Insights'
                        : 'Generate Competitive Insights'}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* ─── Tab 2: Competitive Insights ─────────────────────────── */}
        {activeTab === 'insights' &&
          (hasAnalysisData || analysisWorkflowRunning || generateMutation.isPending) && (
            <>
              {/* Workflow running card */}
              {(analysisWorkflowRunning || generateMutation.isPending) && (
                <div className={patterns.workflowCard}>
                  <div className={patterns.workflowSpinner} />
                  <div className={patterns.workflowTitle}>Running Competitive Analysis</div>
                  <div className={patterns.workflowSubtext}>
                    Analyzing competitors, reviews, and market positioning. Check back in 5–10
                    minutes.
                  </div>
                </div>
              )}

              {analysisOutOfDate && (
                <Callout
                  variant="warning"
                  title="These insights are out of date"
                  actions={
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => generateMutation.mutate()}
                      disabled={generateBlocked}
                    >
                      Regenerate
                    </Button>
                  }
                >
                  They were generated for a different set of competitors than the ones picked now,
                  or are past their refresh date. Regenerate to analyse your current picks.
                </Callout>
              )}

              {/* Only show analysis content when not generating */}
              {!analysisWorkflowRunning && !generateMutation.isPending && (
                <>
                  {/* Competitor Cards with analysis */}
                  {(() => {
                    const renderCard = (c: (typeof analyzedCompetitors)[number]) => {
                      const threat = getThreatLevel(c);
                      return (
                        <Card key={c.id} className={cs.insightCard}>
                          <div className={cs.insightCardInner}>
                            <div className={cs.insightCardLeft}>
                              <div className={cs.insightCardNameRow}>
                                <span className={cs.insightCardName}>{c.name}</span>
                                <Badge
                                  variant={
                                    threat === 'HIGH'
                                      ? 'error'
                                      : threat === 'MED'
                                        ? 'warning'
                                        : 'neutral'
                                  }
                                >
                                  {threat}
                                </Badge>
                                {c.distanceMiles != null && (
                                  <span className={cs.insightCardDistanceMi}>
                                    {c.distanceMiles.toFixed(1)} mi
                                  </span>
                                )}
                              </div>
                              <div className={cs.insightCardMeta}>
                                {c.address && <span>{c.address} · </span>}
                                {c.rating != null && (
                                  <span className={cs.insightCardRating}>
                                    {c.rating}
                                    <Star size={11} fill="currentColor" aria-hidden /> ·{' '}
                                  </span>
                                )}
                                {c.reviewCount != null && <span>{c.reviewCount} reviews</span>}
                              </div>
                              <div className={cs.insightCardStrengths}>
                                <span className={cs.insightCardStrengthsLabel}>Strengths: </span>
                                {c.strengths ? (
                                  <ul className={cs.insightCardBulletList}>
                                    {c.strengths.split(' | ').map((s, i) => (
                                      <li key={i} className={cs.insightCardBulletItem}>
                                        {s}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span className={cs.insightCardNoData}>
                                    No notable strengths identified
                                  </span>
                                )}
                              </div>
                              <div className={cs.insightCardGaps}>
                                <span className={cs.insightCardGapsLabel}>Gaps: </span>
                                {c.gap ? (
                                  <ul className={cs.insightCardBulletList}>
                                    {c.gap.split(' | ').map((g: string, i: number) => (
                                      <li key={i} className={cs.insightCardBulletItem}>
                                        {g}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span className={cs.insightCardNoData}>
                                    No exploitable gaps identified
                                  </span>
                                )}
                              </div>
                            </div>
                            {c.website && (
                              <a
                                href={c.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cs.insightCardWebsiteLink}
                              >
                                Website ↗
                              </a>
                            )}
                          </div>
                        </Card>
                      );
                    };

                    if (isSpecialist) {
                      const directAnalyzed = analyzedCompetitors.filter(
                        (c) => c.competitorLayer === 'DIRECT' || !c.competitorLayer,
                      );
                      const indirectAnalyzed = analyzedCompetitors.filter(
                        (c) => c.competitorLayer === 'INDIRECT',
                      );
                      return (
                        <>
                          {directAnalyzed.length > 0 && (
                            <>
                              <div className={cs.insightLayerHeader}>
                                Direct Competitors ({directAnalyzed.length})
                              </div>
                              {directAnalyzed.map(renderCard)}
                            </>
                          )}
                          {indirectAnalyzed.length > 0 && (
                            <>
                              <div className={cs.insightLayerHeaderIndirect}>
                                Indirect Competitors ({indirectAnalyzed.length})
                              </div>
                              {indirectAnalyzed.map(renderCard)}
                            </>
                          )}
                        </>
                      );
                    }

                    return <>{analyzedCompetitors.map(renderCard)}</>;
                  })()}

                  {/* Key Competitive Insight */}
                  <Card className={`${styles.cardGlow} ${cs.glowCard}`}>
                    <div className={cs.glowCardTitle}>Key Competitive Insight</div>
                    <div>
                      <div className={cs.glowCardHeadline}>{analysis!.insight!.headline}</div>
                      <div className={cs.glowCardEvidenceWrap}>
                        {analysis!.insight!.supportingEvidence.map((evidence, i) => (
                          <div key={i} className={cs.glowCardEvidenceItem}>
                            <span className={cs.glowCardEvidenceBullet}>•</span>
                            {evidence}
                          </div>
                        ))}
                      </div>
                      <div className={cs.glowCardImplication}>
                        {analysis!.insight!.strategicImplication}
                      </div>
                    </div>
                  </Card>

                  {/* Saved feedback display */}
                  {!showFeedback && analysis!.feedback && (
                    <Card className={cs.feedbackCard}>
                      <div className={cs.feedbackCardHeading}>Your Feedback</div>
                      <div className={cs.feedbackCardText}>{analysis!.feedback}</div>
                      <div className={cs.feedbackCardActions}>
                        <button
                          onClick={() => {
                            setFeedbackText(analysis!.feedback ?? '');
                            setShowFeedback(true);
                          }}
                          className={cs.feedbackBtn}
                        >
                          Edit Feedback
                        </button>
                      </div>
                    </Card>
                  )}

                  {/* Share Feedback button (no feedback yet) */}
                  {!showFeedback && !analysis!.feedback && (
                    <div className={cs.shareFeedbackWrap}>
                      <button
                        onClick={() => {
                          setFeedbackText('');
                          setShowFeedback(true);
                        }}
                        className={cs.shareFeedbackBtn}
                      >
                        Share Feedback
                      </button>
                    </div>
                  )}

                  {/* Feedback editor */}
                  {showFeedback && (
                    <Card className={cs.feedbackEditorCard}>
                      <div className={cs.feedbackEditorHint}>
                        Share corrections or additional context about your competition (max{' '}
                        {MAX_FEEDBACK_WORDS} words). This will be used when generating your
                        strategy.
                      </div>
                      <textarea
                        value={feedbackText}
                        onChange={(e) => {
                          const words = e.target.value.trim().split(/\s+/);
                          if (e.target.value.trim() === '' || words.length <= MAX_FEEDBACK_WORDS) {
                            setFeedbackText(e.target.value);
                          }
                        }}
                        placeholder="e.g. The competitor down the street actually closed last month, or: We are the only practice offering sedation in this area..."
                        className={cs.feedbackTextarea}
                      />
                      <div className={cs.feedbackEditorFooter}>
                        <span
                          className={overLimit ? cs.feedbackWordCountOver : cs.feedbackWordCount}
                        >
                          {wordCount}/{MAX_FEEDBACK_WORDS} words
                        </span>
                        <div className={cs.feedbackEditorBtns}>
                          <button onClick={() => setShowFeedback(false)} className={cs.cancelBtn}>
                            Cancel
                          </button>
                          <button
                            onClick={() => feedbackMutation.mutate(feedbackText.trim())}
                            disabled={
                              !feedbackText.trim() || overLimit || feedbackMutation.isPending
                            }
                            className={
                              !feedbackText.trim() || overLimit || feedbackMutation.isPending
                                ? cs.saveFeedbackBtnDisabled
                                : cs.saveFeedbackBtn
                            }
                          >
                            {feedbackMutation.isPending ? 'Saving...' : 'Save Feedback'}
                          </button>
                        </div>
                      </div>
                    </Card>
                  )}
                </>
              )}
            </>
          )}
      </>
    </div>
  );
}
